import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Upload, MessageCircle, Check } from "lucide-react";
import { useLanguage } from "../../i18n";
import { API } from "../../constants";
import { trackSimulationFormSubmit, trackSimulationFormConversion, trackWhatsAppConversion } from "../../utils/analytics";

const VisualizaCampanaSection = () => {
  const { t, language } = useLanguage();
  const [formData, setFormData] = useState({
    projectName: '',
    description: '',
    projectType: '',
    city: '',
    date: '',
    budget: '',
    whatsapp: '',
    email: ''
  });
  const [uploadedFile, setUploadedFile] = useState(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const projectTypes = t('visualize.projectTypes') || [];
  const cities = t('visualize.cities') || [];
  const budgets = t('visualize.budgetRanges') || [];
  const exampleCards = t('visualize.examples.cards') || [];

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadedFile(file);
    }
  };

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      
      // Convert file to base64 if exists
      let fileData = null;
      let fileType = null;
      if (uploadedFile) {
        fileData = await fileToBase64(uploadedFile);
        fileType = uploadedFile.type;
      }
      
      const response = await axios.post(`${API_URL}/api/simulations`, {
        project_name: formData.projectName,
        description: formData.description,
        project_type: formData.projectType,
        city: formData.city,
        event_date: formData.date,
        budget: formData.budget,
        whatsapp: formData.whatsapp,
        email: formData.email,
        file_name: uploadedFile ? uploadedFile.name : null,
        file_data: fileData,
        file_type: fileType
      });
      
      if (response.data) {
        // Track Google Ads conversion
        await trackSimulationFormConversion(formData);
        trackSimulationFormSubmit(formData);
        toast.success(t('visualize.toastSuccess'));
        setIsSubmitted(true);
      }
    } catch (error) {
      console.error('Error submitting simulation:', error);
      toast.error(t('visualize.toastError'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleWhatsAppRedirect = () => {
    const msgTemplate = t('visualize.success.whatsappMessage') || '';
    const message = encodeURIComponent(msgTemplate.replace('{projectName}', formData.projectName));
    const whatsappUrl = `https://wa.me/525578535742?text=${message}`;
    trackWhatsAppConversion(whatsappUrl, 'simulation_success');
  };

  if (isSubmitted) {
    return (
      <section id="visualiza" className="py-24 md:py-32 bg-[#030810]" data-testid="visualiza-section">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <div className="bg-[#0a1628] border border-cyan-500/20 rounded-2xl p-12">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
              <Check size={40} className="text-white" />
            </div>
            <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
              {t('visualize.success.title')}
            </h3>
            <p className="text-gray-400 mb-8">
              {t('visualize.success.subtitle')}
            </p>
            <button
              onClick={handleWhatsAppRedirect}
              className="inline-flex items-center gap-3 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-semibold py-4 px-8 rounded-full transition-all"
            >
              <MessageCircle size={20} />
              {t('visualize.success.whatsappButton')}
            </button>
          </div>
        </div>
      </section>
    );
  }

  // Example card images
  const cardImages = [
    "https://customer-assets.emergentagent.com/job_aerial-performance/artifacts/bg8ocw8f_74F58C84-108C-4111-92C9-6C7C11811846.png",
    "https://customer-assets.emergentagent.com/job_aerial-performance/artifacts/yvz483u6_IMG_0932.png",
    "https://customer-assets.emergentagent.com/job_aerial-performance/artifacts/6nt8cq0a_IMG_0933.png",
    "https://customer-assets.emergentagent.com/job_aerial-performance/artifacts/nsou4hsm_IMG_0931.png",
    "https://customer-assets.emergentagent.com/job_aerial-performance/artifacts/ccrceslf_AB60811A-CF87-4985-9F32-9CFD929A8346.png",
    "https://customer-assets.emergentagent.com/job_aerial-performance/artifacts/5igk53be_6fc0c632-7e5f-4f68-aaad-2bb000eb7a3b.jpeg",
  ];

  return (
    <section id="visualiza" className="py-16 md:py-24 bg-[#030810]" data-testid="visualiza-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Main 2-column layout */}
        <div className="grid lg:grid-cols-5 gap-8 lg:gap-12 items-start">
          {/* Left Column - Text + Form (2/5) */}
          <div className="lg:col-span-2">
            {/* Header */}
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4 leading-tight">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-blue-500 to-cyan-400">
                {language === 'es' ? 'Visualiza tu\ncampaña en\nel cielo' : 'Visualize your\ncampaign in\nthe sky'}
              </span>
            </h2>
            <p className="text-gray-400 text-lg mb-8 max-w-md">
              {t('visualize.subtitle')}
            </p>

            {/* Form Card */}
            <div className="bg-[#0a1628]/80 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
              <h3 className="text-xl font-semibold text-white mb-2">{t('visualize.formTitle')}</h3>
              <p className="text-gray-400 text-sm mb-6">
                {t('visualize.formSubtitle')}
              </p>

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Project Name */}
                <div>
                  <label className="block text-white text-sm mb-2">{t('visualize.form.projectName')}</label>
                  <input
                    type="text"
                    placeholder={t('visualize.form.projectNamePlaceholder')}
                    className="w-full bg-[#050d1a] border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500/50"
                    value={formData.projectName}
                    onChange={(e) => setFormData({...formData, projectName: e.target.value})}
                    required
                  />
                </div>

                {/* File Upload */}
                <div>
                  <label className="block text-white text-sm mb-2">{t('visualize.form.uploadLabel')}</label>
                  <label className="flex flex-col items-center justify-center w-full h-24 border-2 border-dashed border-white/20 rounded-lg cursor-pointer hover:border-cyan-500/50 transition-colors bg-[#050d1a]">
                    <Upload size={24} className="text-gray-400 mb-2" />
                    <span className="text-gray-400 text-sm">{uploadedFile ? uploadedFile.name : t('visualize.form.uploadButton')}</span>
                    <span className="text-gray-500 text-xs">{t('visualize.form.uploadHint')}</span>
                    <input type="file" className="hidden" accept=".png,.jpg,.jpeg,.svg" onChange={handleFileChange} />
                  </label>
                </div>

                {/* Description */}
                <div>
                  <label className="block text-white text-sm mb-2">{t('visualize.form.description')}</label>
                  <textarea
                    placeholder={t('visualize.form.descriptionPlaceholder')}
                    rows={3}
                    className="w-full bg-[#050d1a] border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500/50 resize-none"
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                  />
                </div>

                {/* Two columns */}
                <div className="grid grid-cols-2 gap-4">
                  {/* Project Type */}
                  <div>
                    <label className="block text-white text-sm mb-2">{t('visualize.form.projectType')}</label>
                    <select
                      className="w-full bg-[#050d1a] border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-cyan-500/50 appearance-none"
                      value={formData.projectType}
                      onChange={(e) => setFormData({...formData, projectType: e.target.value})}
                    >
                      <option value="" className="text-gray-500">{t('visualize.form.selectType')}</option>
                      {Array.isArray(projectTypes) && projectTypes.map((type) => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>

                  {/* City */}
                  <div>
                    <label className="block text-white text-sm mb-2">{t('visualize.form.city')}</label>
                    <select
                      className="w-full bg-[#050d1a] border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-cyan-500/50 appearance-none"
                      value={formData.city}
                      onChange={(e) => setFormData({...formData, city: e.target.value})}
                    >
                      <option value="" className="text-gray-500">{t('visualize.form.cityPlaceholder')}</option>
                      {Array.isArray(cities) && cities.map((city) => (
                        <option key={city} value={city}>{city}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Two columns */}
                <div className="grid grid-cols-2 gap-4">
                  {/* Date */}
                  <div>
                    <label className="block text-white text-sm mb-2">{t('visualize.form.date')}</label>
                    <input
                      type="date"
                      className="w-full bg-[#050d1a] border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-cyan-500/50"
                      value={formData.date}
                      onChange={(e) => setFormData({...formData, date: e.target.value})}
                    />
                  </div>

                  {/* Budget */}
                  <div>
                    <label className="block text-white text-sm mb-2">{t('visualize.form.budget')}</label>
                    <select
                      className="w-full bg-[#050d1a] border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-cyan-500/50 appearance-none"
                      value={formData.budget}
                      onChange={(e) => setFormData({...formData, budget: e.target.value})}
                    >
                      <option value="" className="text-gray-500">{t('visualize.form.selectBudget')}</option>
                      {Array.isArray(budgets) && budgets.map((budget) => (
                        <option key={budget} value={budget}>{budget}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Two columns */}
                <div className="grid grid-cols-2 gap-4">
                  {/* WhatsApp */}
                  <div>
                    <label className="block text-white text-sm mb-2">{t('visualize.form.whatsapp')}</label>
                    <input
                      type="tel"
                      placeholder={t('visualize.form.whatsappPlaceholder')}
                      className="w-full bg-[#050d1a] border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500/50"
                      value={formData.whatsapp}
                      onChange={(e) => setFormData({...formData, whatsapp: e.target.value})}
                      required
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-white text-sm mb-2">{t('visualize.form.email')}</label>
                    <input
                      type="email"
                      placeholder={t('visualize.form.emailPlaceholder')}
                      className="w-full bg-[#050d1a] border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500/50"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      required
                    />
                  </div>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-semibold py-4 rounded-xl transition-all disabled:opacity-50"
                >
                  {isLoading ? t('visualize.form.sending') : t('visualize.form.submit')}
                </button>
              </form>
            </div>
          </div>

          {/* Right Column - Visual + Examples (3/5) */}
          <div className="lg:col-span-3 space-y-8">
            {/* Main Visual - Tu proyecto en el cielo */}
            <div className="relative rounded-2xl overflow-hidden">
              <img 
                src="https://customer-assets.emergentagent.com/job_aerial-performance/artifacts/0rgr7il6_IMG_0934.png"
                alt="Tu proyecto en el cielo"
                className="w-full h-auto"
              />
            </div>

            {/* Examples Section */}
            <div>
              <h3 className="text-2xl md:text-3xl font-semibold text-white text-center mb-2">
                {t('visualize.examples.title')}
              </h3>
              <p className="text-gray-400 text-center text-sm mb-6">
                {t('visualize.examples.subtitle')}
              </p>

              {/* 3 Cards - Row 1 */}
              <div className="grid grid-cols-3 gap-4 mb-4">
                {Array.isArray(exampleCards) && exampleCards.slice(0, 3).map((card, index) => (
                  <div key={index} className="text-center">
                    <div className="rounded-xl overflow-hidden mb-3 aspect-[4/3]">
                      <img 
                        src={cardImages[index]}
                        alt={card.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <h4 className="text-white font-semibold text-sm mb-1">{card.title}</h4>
                    <p className="text-gray-400 text-xs leading-relaxed">{card.description}</p>
                  </div>
                ))}
              </div>

              {/* 3 Cards - Row 2 */}
              <div className="grid grid-cols-3 gap-4">
                {Array.isArray(exampleCards) && exampleCards.slice(3, 6).map((card, index) => (
                  <div key={index + 3} className="text-center">
                    <div className="rounded-xl overflow-hidden mb-3 aspect-[4/3]">
                      <img 
                        src={cardImages[index + 3]}
                        alt={card.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <h4 className="text-white font-semibold text-sm mb-1">{card.title}</h4>
                    <p className="text-gray-400 text-xs leading-relaxed">{card.description}</p>
                  </div>
                ))}
              </div>

              {/* Footer text */}
              <div className="text-center mt-6">
                <p className="text-gray-400 text-sm">
                  {t('visualize.examples.footer')}
                </p>
                <p className="text-gray-500 text-xs">
                  {t('visualize.examples.footerSub')}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Disclaimer */}
        <div className="mt-12 pt-8 border-t border-white/10">
          <p className="text-gray-500 text-xs text-center leading-relaxed max-w-4xl mx-auto">
            {t('visualize.disclaimer')}
          </p>
          <p className="text-center mt-4">
            <button 
              onClick={() => document.getElementById('terms-modal').showModal()}
              className="text-cyan-500 hover:text-cyan-400 text-xs underline transition-colors"
            >
              {t('visualize.termsLink')}
            </button>
          </p>
        </div>

        {/* Terms Modal */}
        <dialog id="terms-modal" className="bg-[#0a1628] text-white rounded-2xl p-8 max-w-2xl backdrop:bg-black/70">
          <div className="space-y-4">
            <h3 className="text-xl font-bold text-cyan-400">{t('terms.title')}</h3>
            {Array.isArray(t('terms.content')) && t('terms.content').map((text, index) => (
              <p key={index} className="text-gray-300 text-sm leading-relaxed">{text}</p>
            ))}
            <div className="pt-4">
              <button 
                onClick={() => document.getElementById('terms-modal').close()}
                className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-semibold py-2 px-6 rounded-full text-sm"
              >
                {t('terms.close')}
              </button>
            </div>
          </div>
        </dialog>
      </div>
    </section>
  );
};

export default VisualizaCampanaSection;
